// The policy editor's draft model (spec 14 §2.4, §4.3): what a reader has typed
// into the matrix and not yet saved, and how it turns into `PUT` bodies. Pure,
// so the diff summary and the request bodies are unit-tested.

import type { components } from "@assessly/api-client";
import {
  majorToMinor,
  minorToMajorText,
  type AgentPolicyItem,
  type PolicyAutonomy,
  type PolicyParamsShape,
} from "./agents-lib";

type PolicyParams = components["schemas"]["AgentPolicyParams"];

export interface FacilityCapDraft {
  readonly facilityId: string;
  readonly cap: string;
}

/** The numbers as typed: text, so a half-typed amount is kept, not lost. */
export interface NumbersDraft {
  readonly cap: string;
  readonly reliability: string;
  readonly facilities: readonly FacilityCapDraft[];
}

export interface RowDraft {
  readonly autonomy: PolicyAutonomy;
  readonly numbers: NumbersDraft;
}

export function numbersOf(params: PolicyParams, digits: number): NumbersDraft {
  if (params === null) return { cap: "", reliability: "", facilities: [] };
  if ("facility_caps" in params) {
    return {
      cap: "",
      reliability: "",
      facilities: params.facility_caps.map((f) => ({
        facilityId: f.facility_id,
        cap: minorToMajorText(f.cap_minor, digits),
      })),
    };
  }
  return {
    cap: minorToMajorText(params.cap_minor, digits),
    reliability: params.min_reliability === undefined ? "" : String(params.min_reliability),
    facilities: [],
  };
}

export function rowDraftOf(item: AgentPolicyItem, digits: number): RowDraft {
  return { autonomy: item.autonomy, numbers: numbersOf(item.policy_params, digits) };
}

export function sameNumbers(a: NumbersDraft, b: NumbersDraft): boolean {
  const caps = (d: NumbersDraft) =>
    d.facilities.map((f) => `${f.facilityId}=${f.cap.trim()}`).join("|");
  return (
    a.cap.trim() === b.cap.trim() &&
    a.reliability.trim() === b.reliability.trim() &&
    caps(a) === caps(b)
  );
}

export type ParamsResult =
  { readonly ok: true; readonly params: PolicyParams } | { readonly ok: false };

/**
 * The typed numbers as the `policy_params` the tool's shape takes. Blank
 * numbers on a cap shape mean "no numbers" (null), which the gate reads as
 * asking; anything that does not parse is refused here, before the request.
 */
export function paramsOf(
  shape: PolicyParamsShape,
  numbers: NumbersDraft,
  currency: string,
  digits: number,
  normalize: (text: string) => string = (text) => text,
): ParamsResult {
  if (shape === "none") return { ok: true, params: null };
  if (shape === "facility_caps") {
    const caps = [];
    for (const f of numbers.facilities) {
      const minor = majorToMinor(normalize(f.cap), digits);
      if (minor === undefined) return { ok: false };
      caps.push({ facility_id: f.facilityId, cap_minor: minor, cap_currency: currency });
    }
    return { ok: true, params: { facility_caps: caps } };
  }
  if (numbers.cap.trim() === "" && numbers.reliability.trim() === "")
    return { ok: true, params: null };
  const capMinor = majorToMinor(normalize(numbers.cap), digits);
  if (capMinor === undefined) return { ok: false };
  if (shape === "cap_reliability" && numbers.reliability.trim() !== "") {
    const text = numbers.reliability.trim();
    if (!/^\d{1,3}$/u.test(text) || Number(text) > 100) return { ok: false };
    return {
      ok: true,
      params: { cap_minor: capMinor, cap_currency: currency, min_reliability: Number(text) },
    };
  }
  return { ok: true, params: { cap_minor: capMinor, cap_currency: currency } };
}

export interface DraftChange {
  readonly toolCode: string;
  readonly fromAutonomy: PolicyAutonomy;
  readonly toAutonomy: PolicyAutonomy;
  readonly numbersChanged: boolean;
}

/** Every row whose draft differs from what the server holds, in matrix order. */
export function draftChanges(
  items: readonly AgentPolicyItem[],
  drafts: ReadonlyMap<string, RowDraft>,
  digits: number,
): DraftChange[] {
  const out: DraftChange[] = [];
  for (const item of items) {
    const draft = drafts.get(item.tool_code);
    if (draft === undefined) continue;
    const base = rowDraftOf(item, digits);
    const numbersChanged = !sameNumbers(base.numbers, draft.numbers);
    if (draft.autonomy === item.autonomy && !numbersChanged) continue;
    out.push({
      toolCode: item.tool_code,
      fromAutonomy: item.autonomy,
      toAutonomy: draft.autonomy,
      numbersChanged,
    });
  }
  return out;
}
